// src/auth/AuthProvider.jsx
import React, { createContext, useEffect, useMemo, useState } from "react";
import { createKeycloakClient } from "./keycloak";
import {
  AUTH_MODES,
  assertKeycloakEnv,
  getAuthMode,
  getDemoAuthEnv,
  getKeycloakEnv,
  validateDemoCredentials,
} from "./authConfig";

export const AuthContext = createContext(null);

const DEMO_SESSION_STORAGE_KEY = "escort_demo_authenticated";

const BYPASS_PROFILE = Object.freeze({
  preferred_username: "DEV_BYPASS",
  roles: ["admin"],
});

function readDemoSession() {
  try {
    return window.sessionStorage.getItem(DEMO_SESSION_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeDemoSession(value) {
  try {
    if (value) {
      window.sessionStorage.setItem(DEMO_SESSION_STORAGE_KEY, "true");
      return;
    }

    window.sessionStorage.removeItem(DEMO_SESSION_STORAGE_KEY);
  } catch {
    // Session persistence is best-effort only.
  }
}

export default function AuthProvider({ children }) {
  const mode = getAuthMode(); // "bypass" | "demo" | "keycloak"
  const isBypass = mode === AUTH_MODES.BYPASS;
  const isDemo = mode === AUTH_MODES.DEMO;
  const isKeycloak = mode === AUTH_MODES.KEYCLOAK;

  const [ready, setReady] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);

  // local auth modes only
  const [bypassAuthenticated, setBypassAuthenticated] = useState(false);
  const [demoAuthenticated, setDemoAuthenticated] = useState(readDemoSession);

  // keycloak instance only for keycloak mode
  const [keycloak, setKeycloak] = useState(null);

  useEffect(() => {
    let intervalId = null;

    if (!isKeycloak) {
      // bypass/demo: never initialize keycloak; the app is immediately ready
      setKeycloak(null);
      setAuthenticated(false);
      setReady(true);
      return undefined;
    }

    try {
      const env = getKeycloakEnv();
      assertKeycloakEnv(env);

      const kc = createKeycloakClient(env);
      setKeycloak(kc);

      kc.onTokenExpired = async () => {
        try {
          await kc.updateToken(60);
        } catch {
          // noop: consumer can call login again if needed
        }
      };

      kc.init({
        onLoad: "check-sso",
        pkceMethod: "S256",
        flow: "standard",
        checkLoginIframe: false,
      })
        .then((auth) => {
          setAuthenticated(Boolean(auth));
          setReady(true);
        })
        .catch((err) => {
          console.error("[Auth] Keycloak init failed:", err);
          setReady(true);
        });

      intervalId = setInterval(async () => {
        try {
          if (kc.authenticated) {
            await kc.updateToken(60);
          }
        } catch {
          // noop
        }
      }, 30000);
    } catch (err) {
      console.error(err);
      setReady(true);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isKeycloak]);

  const demoCredentials = getDemoAuthEnv();

  const effectiveAuthenticated = isBypass
    ? bypassAuthenticated
    : isDemo
      ? demoAuthenticated
      : authenticated;

  const value = useMemo(
    () => ({
      mode,
      ready,
      authenticated: effectiveAuthenticated,
      keycloak: isKeycloak ? keycloak : null,

      token: isBypass
        ? bypassAuthenticated
          ? "DEV_BYPASS_TOKEN"
          : null
        : isDemo
          ? demoAuthenticated
            ? "DEMO_AUTH_TOKEN"
            : null
          : keycloak?.token || null,

      profile: isBypass
        ? bypassAuthenticated
          ? BYPASS_PROFILE
          : null
        : isDemo
          ? demoAuthenticated
            ? {
                preferred_username: demoCredentials.username,
                roles: ["user"],
              }
            : null
          : keycloak?.tokenParsed || null,

      login: async (credentials = {}) => {
        if (isBypass) {
          setBypassAuthenticated(true);
          return { ok: true };
        }

        if (isDemo) {
          if (!validateDemoCredentials(credentials)) {
            return { ok: false, error: "Invalid username or password." };
          }

          writeDemoSession(true);
          setDemoAuthenticated(true);
          return { ok: true };
        }

        keycloak?.login({
          redirectUri: `${window.location.origin}/landing`,
          ...credentials,
        });

        return { ok: true };
      },

      register: async (opts = {}) => {
        if (isBypass) {
          setBypassAuthenticated(true);
          return { ok: true };
        }

        if (isDemo) {
          return {
            ok: false,
            error: "Registration is disabled in demo mode.",
          };
        }

        keycloak?.register({
          redirectUri: `${window.location.origin}/landing`,
          ...opts,
        });

        return { ok: true };
      },

      logout: () => {
        if (isBypass) {
          setBypassAuthenticated(false);
          return;
        }

        if (isDemo) {
          writeDemoSession(false);
          setDemoAuthenticated(false);
          return;
        }

        keycloak?.logout({ redirectUri: window.location.origin });
      },

      hasRole: (role) => {
        if (isBypass) return bypassAuthenticated;
        if (isDemo) return demoAuthenticated && role === "user";
        return keycloak?.hasRealmRole?.(role) || false;
      },
    }),
    [
      mode,
      ready,
      effectiveAuthenticated,
      bypassAuthenticated,
      demoAuthenticated,
      demoCredentials.username,
      isBypass,
      isDemo,
      isKeycloak,
      keycloak,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
